import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useOrder } from "../context/OrderContext";
import { PromoHeroBanner } from "../components/PromoBanner";
import ProductGallery from "../components/ProductGallery";
import api from "../lib/api";
import {
  CheckCircle2, Ruler, Hammer, Truck, Shield, MapPin,
  ArrowRight, Sparkles, Wrench, ChevronRight, Phone, Star,
} from "lucide-react";

const SHOP_HOURS = "Mon–Fri 9am–5pm";
const SHOP_PHONE = "(877) 400-0889";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const resolveSrc = (u) => (!u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`);

// Install photos used to be hardcoded; now they come from the admin-managed
// `install_photos` collection so the user can upload + label new shots
// without a redeploy. Returns [] until the API responds.

const TIER_CARDS = [
  {
    name: "Durango",
    slug: "sliding-door-durango",
    tagline: "Most Affordable",
    price: 269,
    img: "https://customer-assets.emergentagent.com/job_dee13181-0281-4e31-92f0-b55b1a9a8de6/artifacts/kx5hnz4k_White24.jpg",
    plain: "The friendly entry-level slider. Lightweight, looks clean, gets the job done. Perfect if you want a real custom fit without paying for upgrades you won't notice.",
    features: [
      "1-3/4\" extruded aluminum frame",
      "Tension rollers (top & bottom)",
      "Vinyl bug seal",
    ],
    cta: "Affordable everyday",
    accent: false,
  },
  {
    name: "Century",
    slug: "sliding-door-century",
    tagline: "Most Popular",
    price: 339,
    img: "https://customer-assets.emergentagent.com/job_dee13181-0281-4e31-92f0-b55b1a9a8de6/artifacts/z9bt180k_White390.jpg",
    plain: "Durango's bigger, beefier sibling. Heavier frame, smoother glide, rollers built for kids, dogs, and breezy summers. The sweet spot most people land on.",
    features: [
      "2-1/2\" heavier extruded frame",
      "1-1/4\" axe-and-bolt bottom rollers",
      "Tension spring rollers up top",
    ],
    cta: "Best balance of build + price",
    accent: true,
  },
  {
    name: "Premier",
    slug: "sliding-door-premier",
    tagline: "Top of the Line",
    price: 399,
    img: "https://customer-assets.emergentagent.com/job_dee13181-0281-4e31-92f0-b55b1a9a8de6/artifacts/mumnjaaf_White26.jpg",
    plain: "Pure luxury. Stainless rollers, heavyweight hat-style frame, mechanically-fastened corners. Slides like butter, looks gorgeous, outlasts everything else on the rail.",
    features: [
      "Hat-style aluminum frame",
      "Stainless steel rollers",
      "Mechanically-fastened corners",
    ],
    cta: "Built for the long haul",
    accent: false,
  },
];

export default function LandingPage() {
  const { state } = useOrder();
  // Show a friendly "continue where you left off" pill if their funnel state
  // is non-trivial (have a product picked already).
  const hasInProgress = !!state?.product?.slug && state?.stepId !== "done";

  // Live "In the wild" gallery — admin manages via /admin/gallery.
  const [installPhotos, setInstallPhotos] = useState([]);
  useEffect(() => {
    api.get("/install-photos")
      .then((r) => setInstallPhotos(r.data || []))
      .catch(() => setInstallPhotos([]));
  }, []);

  // Per-tier extra photos AND live prices — admin manages products via
  // /admin/products. We merge by product slug so the homepage cards, hero
  // copy, and Century callout all reflect whatever the admin set, without
  // any hardcoded prices going stale.
  const [galleryBySlug, setGalleryBySlug] = useState({});
  const [priceBySlug, setPriceBySlug] = useState({});
  useEffect(() => {
    api.get("/products")
      .then((r) => {
        const gMap = {};
        const pMap = {};
        (r.data || []).forEach((p) => {
          gMap[p.slug] = p.gallery_images || [];
          if (typeof p.base_price === "number") pMap[p.slug] = p.base_price;
        });
        setGalleryBySlug(gMap);
        setPriceBySlug(pMap);
      })
      .catch(() => {
        setGalleryBySlug({});
        setPriceBySlug({});
      });
  }, []);

  // Lowest sliding-door price for the hero "Custom doors from $X" line.
  // Falls back to the hardcoded TIER_CARDS prices if the API hasn't
  // responded yet, so the homepage never flashes "$undefined".
  const doorPrices = Object.entries(priceBySlug)
    .filter(([slug]) => slug.startsWith("sliding-door-"))
    .map(([, p]) => p);
  const minDoorPrice = doorPrices.length
    ? Math.min(...doorPrices)
    : Math.min(...TIER_CARDS.map((t) => t.price));
  const centuryPrice = priceBySlug["sliding-door-century"]
    ?? TIER_CARDS.find((t) => t.slug === "sliding-door-century")?.price
    ?? 339;
  const windowPrice = priceBySlug["window-screen"] ?? 70;

  return (
    <div className="min-h-screen" style={{ background: "#f7faf6" }}>
      <PromoHeroBanner />
      {/* ---------- Top nav ---------- */}
      <header className="sticky top-0 z-30 border-b bg-white/80 backdrop-blur">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
            <img
              src="https://customer-assets.emergentagent.com/job_tech-dispatch-31/artifacts/a6ta9f48_SCREENZZ-LOGO-RGB_TextOnly.png"
              alt="ScreenzZ"
              className="h-8"
            />
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm font-semibold">
            <a href="#how-it-works" className="muted-text hover:brand-text">How it works</a>
            <a href="#installs" className="muted-text hover:brand-text">Real installs</a>
            <a href="#doors" className="muted-text hover:brand-text">Sliding doors</a>
            <a href="#windows" className="muted-text hover:brand-text">Window screens</a>
            <Link to="/parts" className="muted-text hover:brand-text" data-testid="nav-parts">Parts &amp; accessories</Link>
            <Link to="/lookup" className="muted-text hover:brand-text" data-testid="nav-track">Track order</Link>
          </nav>
          <Link to="/order" data-testid="nav-cta" className="cta-btn text-sm" style={{ width: "auto", padding: "8px 20px" }}>
            Start order
          </Link>
        </div>
      </header>

      {/* ---------- Continue-where-you-left-off ---------- */}
      {hasInProgress && (
        <div className="max-w-6xl mx-auto px-5 pt-4">
          <Link
            to="/order"
            data-testid="continue-order-pill"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--brand-green-light)] brand-text text-xs font-semibold hover:bg-[var(--brand-green-light)]/80"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Continue your order: {state.product.name}
            <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}

      {/* ---------- Hero ---------- */}
      <section className="max-w-6xl mx-auto px-5 pt-10 pb-12 md:pt-16 md:pb-20 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider brand-text mb-4">
            <MapPin className="w-3.5 h-3.5" />
            Built in Murrieta · Pickup or local delivery
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[var(--brand-text)] leading-[1.05]">
            Custom screen doors,<br />
            <span className="brand-text">your way.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg muted-text leading-relaxed max-w-xl">
            Same shop, same custom build — now with a DIY-friendly option for folks who don't
            mind grabbing a tape measure. <strong className="text-[var(--brand-text)]" data-testid="hero-from-price">Custom doors from ${minDoorPrice}.</strong>
          </p>

          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to="/choose-your-door"
              data-testid="hero-cta-view-doors"
              className="cta-btn inline-flex items-center"
              style={{ width: "auto" }}
            >
              View doors <ArrowRight className="w-4 h-4 inline-block ml-1" />
            </Link>
            <Link
              to="/order"
              data-testid="hero-cta-start-order"
              className="px-5 py-3 rounded-full border-2 border-[var(--brand-border)] font-semibold text-sm hover:border-[var(--brand-green)] text-[var(--brand-text)]"
            >
              Start order
            </Link>
          </div>

          <a
            href="https://maps.app.goo.gl/qTPaJXLGhznMVeA87?g_st=ic"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-social-proof"
            className="mt-3 inline-flex items-center gap-1.5 text-xs muted-text hover:text-[var(--brand-text)] transition-colors"
          >
            <span className="flex items-center gap-0.5" aria-hidden="true">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="w-3 h-3 fill-[#f5b301] stroke-[#f5b301]" />
              ))}
            </span>
            <span className="underline-offset-2 hover:underline">180+ 5-star reviews</span>
          </a>

          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs muted-text">
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 brand-text" /> Made-to-measure</span>
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 brand-text" /> Free shop pickup</span>
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 brand-text" /> Pro install available</span>
          </div>
        </div>

        <div className="relative">
          <img
            src="https://customer-assets.emergentagent.com/job_dee13181-0281-4e31-92f0-b55b1a9a8de6/artifacts/z9bt180k_White390.jpg"
            alt="Century sliding screen door"
            className="rounded-3xl shadow-2xl border-2 border-[var(--brand-border)] w-full"
            style={{ maxHeight: "560px", objectFit: "cover", objectPosition: "center" }}
          />
          <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-lg border-2 border-[var(--brand-border)] px-4 py-3 max-w-[230px]">
            <p className="text-xs muted-text">Most popular tier</p>
            <p className="font-extrabold text-[var(--brand-text)]" data-testid="hero-century-callout">Century · from ${centuryPrice}</p>
            <p className="text-xs muted-text mt-0.5">A favorite for one- or two-door owners doing it themselves.</p>
          </div>
        </div>
      </section>

      {/* ---------- Three paths to a screen door ---------- */}
      <section className="bg-white border-y border-[var(--brand-border)]">
        <div className="max-w-6xl mx-auto px-5 py-14">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[var(--brand-text)] max-w-3xl">
            Three ways to get a custom screen door. Pick the one that fits you.
          </h2>
          <p className="mt-3 muted-text max-w-2xl">
            Every option has trade-offs in time, cost, and effort. We offer a couple of these
            ourselves — pick the one that fits your project.
          </p>
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            <PathCard
              title="Hardware store, stock sizes"
              tag="Cheapest, fastest"
              body="If your opening is a standard 36×80 or 48×80 and you're not picky about hardware, this works. Many older homes aren't standard, though — so always measure first."
            />
            <PathCard
              title="Build it yourself from raw stock"
              tag="Most rewarding"
              body="If you have a chop saw, spline tools, and an afternoon, you can absolutely build one. It's a satisfying project — but it's a project. Plan on a few hours your first time around."
            />
            <PathCard
              title="Full-service screen company"
              tag="Most convenient"
              body="A pro comes out, measures, builds, and installs. Hands-off and ideal if you've got 3+ doors, an unusual opening, or just don't want to deal. (Yep — we offer this too.)"
            />
          </div>
          <div className="mt-8 rounded-3xl border-2 border-dashed border-[var(--brand-green)] bg-[var(--brand-green-light)]/40 p-6 md:p-8">
            <p className="text-sm font-semibold uppercase tracking-wider brand-text mb-2">Or, the in-between option — what this site is for</p>
            <p className="text-xl sm:text-2xl font-extrabold text-[var(--brand-text)] leading-snug">
              You measure (we'll show you how). We build it custom in our Murrieta shop.
              You pick it up — or we deliver and install for $99.
              <span className="muted-text font-semibold"> Same custom build, friendlier price tag.</span>
            </p>
            <p className="text-sm muted-text mt-3">
              Not feeling confident about measuring? Add Pro Measurement at checkout — a tech
              comes out, gets perfect numbers, and your build goes from there.
            </p>
          </div>
        </div>
      </section>

      {/* ---------- How it works ---------- */}
      <section id="how-it-works" className="max-w-6xl mx-auto px-5 py-14">
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[var(--brand-text)]">
          Three steps. About 2 minutes of your time.
        </h2>
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <StepCard
            n={1}
            icon={<Ruler className="w-6 h-6 brand-text" />}
            title="Measure"
            body="Tape measure + 5 minutes. We send a guide with photos. Not confident? Add Pro Measurement at checkout — a tech comes to your home."
          />
          <StepCard
            n={2}
            icon={<Hammer className="w-6 h-6 brand-text" />}
            title="We build it"
            body="Cut, splined, and fitted to your exact dimensions in our Murrieta shop. Typical lead time: 3–5 business days."
          />
          <StepCard
            n={3}
            icon={<Truck className="w-6 h-6 brand-text" />}
            title="Pick up or get it delivered"
            body="Free pickup at our shop. Local delivery $39. Delivery + professional install $99 — we drop it in for you."
          />
        </div>
      </section>

      {/* ---------- Real install gallery ---------- */}
      <section id="installs" className="bg-white border-y border-[var(--brand-border)]">
        <div className="max-w-6xl mx-auto px-5 py-14">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider brand-text">In the wild</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[var(--brand-text)]">
                Real ScreenzZ Screens and Doors.
              </h2>
              <p className="muted-text mt-2 max-w-xl">
                Not stock photos — these are screens and doors we built and dropped into customers&apos;
                homes around Murrieta.
              </p>
            </div>
          </div>
          {installPhotos.length === 0 ? (
            <p className="muted-text text-sm" data-testid="installs-empty">More install photos coming soon.</p>
          ) : (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {installPhotos.map((p, i) => (
                <figure
                  key={p.id || i}
                  data-testid={`install-photo-${i}`}
                  className="rounded-3xl overflow-hidden border-2 border-[var(--brand-border)] bg-[var(--brand-green-light)]/30"
                >
                  <div className="aspect-[4/5] overflow-hidden">
                    <img
                      src={resolveSrc(p.url)}
                      alt={p.label || ""}
                      loading="lazy"
                      className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-500"
                    />
                  </div>
                  {(p.city || p.label) && (
                    <figcaption className="px-4 py-3 text-xs muted-text">
                      {p.city && (
                        <span className="inline-flex items-center gap-1 font-bold text-[var(--brand-text)] mr-2">
                          <MapPin className="w-3 h-3 brand-text" />
                          {p.city}
                        </span>
                      )}
                      {p.tier && (
                        <span className="inline-block text-[10px] font-bold uppercase tracking-wider brand-text mr-2">
                          {p.tier}
                        </span>
                      )}
                      {p.label}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ---------- Door tiers ---------- */}
      <section id="doors" className="bg-white border-y border-[var(--brand-border)]">
        <div className="max-w-6xl mx-auto px-5 py-14">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider brand-text">Sliding screen doors</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[var(--brand-text)]">
                Three quality tiers. One build philosophy.
              </h2>
              <p className="muted-text mt-2 max-w-xl">
                Same custom-cut frame, scaled hardware. Pick depending on your
                style and the durability you're looking for.
              </p>
            </div>
            <Link to="/choose-your-door" data-testid="see-doors-link" className="text-sm font-semibold brand-text underline-offset-2 hover:underline">
              See all tiers →
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {TIER_CARDS.map((t) => (
              <TierCard
                key={t.name}
                {...t}
                price={priceBySlug[t.slug] ?? t.price}
                galleryImages={galleryBySlug[t.slug] || []}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Window screens ---------- */}
      <section id="windows" className="max-w-6xl mx-auto px-5 py-14 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider brand-text">Window screens</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[var(--brand-text)] mt-1" data-testid="window-headline-price">
            From ${windowPrice} a screen.<br />Bulk discounts kick in at 5+.
          </h2>
          <p className="muted-text mt-3 max-w-lg">
            Custom-built window screens in any size. Pick standard fiberglass mesh,
            UltraView, No-See-Ums, or 80% Solar — at the upgrade tier you actually want,
            not whatever's in stock.
          </p>
          <ul className="mt-5 space-y-2 text-sm">
            <BulkRow qty="5+" save="$25" />
            <BulkRow qty="8+" save="$60" />
            <BulkRow qty="12+" save="$120" />
            <BulkRow qty="20+" save="$200" />
          </ul>
          <Link to="/order" data-testid="windows-cta" className="cta-btn inline-flex items-center mt-7" style={{ width: "auto" }}>
            Order window screens →
          </Link>
        </div>
        <div className="space-y-5">
          <div
            data-testid="window-screens-photo"
            className="rounded-3xl overflow-hidden border-2 border-[var(--brand-border)] bg-[#f5f7f6]"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_dee13181-0281-4e31-92f0-b55b1a9a8de6/artifacts/0smxmx47_Untitled_design.png"
              alt="Custom window screens in multiple sizes leaning against a brick wall — built by ScreenzZ"
              loading="lazy"
              className="w-full h-auto block"
            />
          </div>
          <div className="rounded-3xl bg-[var(--brand-green-light)]/40 p-6 border-2 border-[var(--brand-border)]">
            <h3 className="font-extrabold text-[var(--brand-text)]">Re-screening 3+ at once?</h3>
            <p className="mt-1 muted-text text-sm">
              Add the Measurement &amp; Perfect Fit Guarantee at checkout. We'll measure on-site
              (or check your numbers), and if anything doesn't fit, we re-build it free.
            </p>
            <ul className="mt-3 text-sm space-y-1 muted-text">
              <li>· 3–5 screens · $49</li>
              <li>· 6–10 screens · $79</li>
              <li>· 11+ screens · $99</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ---------- Why ScreenzZ ---------- */}
      <section className="bg-white border-y border-[var(--brand-border)]">
        <div className="max-w-6xl mx-auto px-5 py-14">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[var(--brand-text)]">
            Why ScreenzZ
          </h2>
          <div className="mt-8 grid md:grid-cols-3 gap-5">
            <FeatureCard icon={<Wrench className="w-5 h-5 brand-text" />} title="Built to your measurements">
              Every door and screen is cut to spec — not picked off a shelf. If your opening
              is unusual, that's actually our specialty.
            </FeatureCard>
            <FeatureCard icon={<Shield className="w-5 h-5 brand-text" />} title="Quality you can stand behind">
              Same materials and hardware we use on our full-service installs. Nothing's
              downgraded just because you're ordering it yourself.
            </FeatureCard>
            <FeatureCard icon={<MapPin className="w-5 h-5 brand-text" />} title="Local + transparent">
              Built right here in Murrieta. Free shop pickup, $39 local delivery, $99 for
              delivery + install. No surprise fees.
            </FeatureCard>
          </div>
        </div>
      </section>

      {/* ---------- Final CTA ---------- */}
      <section className="max-w-4xl mx-auto px-5 py-16 text-center">
        <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-[var(--brand-text)]">
          Ready to get measured up?
        </h2>
        <p className="mt-3 muted-text text-lg">
          Two-minute order. Free pickup at the shop. Or we'll deliver and install.
        </p>
        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <Link to="/order" data-testid="final-cta" className="cta-btn inline-flex items-center" style={{ width: "auto", fontSize: "16px", padding: "16px 28px" }}>
            Start your custom order <ArrowRight className="w-4 h-4 inline-block ml-1" />
          </Link>
          <a href={`tel:${SHOP_PHONE.replace(/[^0-9]/g, "")}`}
            data-testid="phone-cta"
            className="px-7 py-4 rounded-full border-2 border-[var(--brand-border)] font-semibold inline-flex items-center gap-2 hover:border-[var(--brand-green)]"
          >
            <Phone className="w-4 h-4" />
            {SHOP_PHONE}
          </a>
        </div>
      </section>

      {/* ---------- Footer ---------- */}
      <footer className="border-t border-[var(--brand-border)] bg-white">
        <div className="max-w-6xl mx-auto px-5 py-10 grid md:grid-cols-3 gap-8 text-sm">
          <div>
            <img
              src="https://customer-assets.emergentagent.com/job_tech-dispatch-31/artifacts/a6ta9f48_SCREENZZ-LOGO-RGB_TextOnly.png"
              alt="ScreenzZ"
              className="h-7"
            />
            <p className="mt-3 muted-text">Custom screen doors and window screens, built locally in Murrieta. Pickup, delivery, or full install.</p>
            <p className="mt-3 muted-text text-xs">
              Questions? Call us {SHOP_HOURS} at{" "}
              <a href={`tel:${SHOP_PHONE.replace(/[^0-9]/g, "")}`} className="brand-text font-semibold hover:underline">
                {SHOP_PHONE}
              </a>
              .
            </p>
          </div>
          <div>
            <p className="font-bold text-[var(--brand-text)] mb-2">Order</p>
            <ul className="space-y-1">
              <li><Link to="/order" className="muted-text hover:brand-text">Start an order</Link></li>
              <li><Link to="/choose-your-door" className="muted-text hover:brand-text">Browse doors</Link></li>
              <li><Link to="/parts" className="muted-text hover:brand-text" data-testid="footer-parts-link">Parts &amp; accessories</Link></li>
              <li><Link to="/lookup" className="muted-text hover:brand-text">Track an order</Link></li>
              <li><Link to="/admin/login" className="muted-text hover:brand-text">Admin</Link></li>
            </ul>
          </div>
          <div>
            <p className="font-bold text-[var(--brand-text)] mb-2">Legal &amp; Contact</p>
            <ul className="space-y-1">
              <li>
                <Link to="/privacy-policy" data-testid="landing-footer-privacy"
                  className="muted-text hover:brand-text">Privacy Policy</Link>
              </li>
              <li>
                <Link to="/terms" data-testid="landing-footer-terms"
                  className="muted-text hover:brand-text">Terms &amp; Conditions</Link>
              </li>
              <li>
                <a href={`tel:${SHOP_PHONE.replace(/[^0-9]/g, "")}`} data-testid="landing-footer-contact"
                  className="muted-text hover:brand-text">Contact ScreenzZ</a>
              </li>
              <li>
                <a href="mailto:brett@screenzz.com"
                  className="muted-text hover:brand-text">brett@screenzz.com</a>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-[var(--brand-border)] py-4 text-center text-xs muted-text">
          © {new Date().getFullYear()} ScreenzZ. All rights reserved.
        </div>
      </footer>
    </div>
  );
}

// ---- Helpers ----

function PathCard({ title, tag, body }) {
  return (
    <div className="rounded-2xl border-2 border-[var(--brand-border)] bg-white p-5">
      <p className="text-[10px] font-bold uppercase tracking-wider brand-text mb-2">{tag}</p>
      <p className="font-bold text-[var(--brand-text)]">{title}</p>
      <p className="mt-2 text-sm muted-text leading-relaxed">{body}</p>
    </div>
  );
}

function StepCard({ n, icon, title, body }) {
  return (
    <div className="rounded-2xl border-2 border-[var(--brand-border)] bg-white p-5 relative">
      <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-[var(--brand-green)] text-white font-extrabold text-sm flex items-center justify-center">
        {n}
      </div>
      <div className="mb-3">{icon}</div>
      <p className="font-bold text-[var(--brand-text)]">{title}</p>
      <p className="mt-1.5 text-sm muted-text leading-relaxed">{body}</p>
    </div>
  );
}

function TierCard({ name, tagline, price, img, plain, features, cta, accent, galleryImages = [] }) {
  return (
    <div
      data-testid={`tier-card-${name.toLowerCase()}`}
      className={`rounded-3xl overflow-hidden border-2 min-w-0 ${accent ? "border-[var(--brand-green)] shadow-lg" : "border-[var(--brand-border)]"} bg-white flex flex-col`}
    >
      <div className="relative">
        <ProductGallery
          mainImage={img}
          images={galleryImages}
          productName={`${name} sliding door`}
        />
        {accent && (
          <span className="absolute top-3 left-3 z-[1] text-[10px] font-extrabold uppercase tracking-wider bg-[var(--brand-green)] text-white px-2 py-1 rounded-full pointer-events-none">
            {tagline}
          </span>
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        {!accent && <p className="text-[11px] font-bold uppercase tracking-wider muted-text">{tagline}</p>}
        <p className="text-xl font-extrabold text-[var(--brand-text)] mt-1">{name}</p>
        <p className="text-sm muted-text">{cta}</p>

        <ul className="mt-3 space-y-1.5 text-xs muted-text">
          {features.map((f, i) => (
            <li key={i} className="flex items-start gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 brand-text mt-0.5 shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        {plain && (
          <div
            data-testid={`tier-plain-${name.toLowerCase()}`}
            className="mt-3 rounded-xl bg-[var(--brand-green-light)]/40 border border-[var(--brand-green-light)] px-3 py-2"
          >
            <p className="text-[11px] font-bold uppercase tracking-wider brand-text mb-1">In plain English</p>
            <p className="text-xs text-[var(--brand-text)] leading-relaxed">{plain}</p>
          </div>
        )}

        <div className="mt-5 flex items-baseline justify-between">
          <span className="text-2xl font-extrabold brand-text">${price}</span>
          <Link
            to="/order"
            data-testid={`tier-cta-${name.toLowerCase()}`}
            className="text-sm font-bold brand-text hover:underline underline-offset-2"
          >
            Start →
          </Link>
        </div>
      </div>
    </div>
  );
}

function BulkRow({ qty, save }) {
  return (
    <li className="flex items-center justify-between rounded-xl bg-white border border-[var(--brand-border)] px-4 py-2.5">
      <span className="muted-text">{qty} screens</span>
      <span className="font-bold brand-text">save {save}</span>
    </li>
  );
}

function FeatureCard({ icon, title, children }) {
  return (
    <div className="rounded-2xl border-2 border-[var(--brand-border)] bg-white p-5">
      <div className="mb-2">{icon}</div>
      <p className="font-bold text-[var(--brand-text)]">{title}</p>
      <p className="mt-1.5 text-sm muted-text leading-relaxed">{children}</p>
    </div>
  );
}

